// src/components/TranscriptionDisplay.tsx
import React, { memo } from "react";
import { TranscriptionData, Patient } from "../types";
import { useTheme } from "../contexts/ThemeContext";

interface TranscriptionDisplayProps {
  transcriptionData: TranscriptionData | null;
  onAnalyze?: () => void;
  processing?: boolean;
  patientData?: Patient | null;
  analysis?: string;
}

const TranscriptionDisplay: React.FC<TranscriptionDisplayProps> = memo(({ 
  transcriptionData, 
  onAnalyze,
  processing = false,
  patientData,
  analysis
}) => {
  const { theme } = useTheme();
  
  if (!transcriptionData?.success) return null;

  const textColor = theme === 'dark' ? '#e2e8f0' : '#1e293b';
  const showAnalyzeButton = onAnalyze && !analysis && !processing;

  return (
    <div className="section-card">
      <h4 style={{ color: textColor }}>Transcription Results</h4>
      {!["en", "english"].includes(transcriptionData.detected_language) && (
        <div className="language-detection">
          <b>Detected Language:</b> {transcriptionData.language_name}
        </div>
      )}
      {!["en", "english"].includes(transcriptionData.detected_language) ? (
        <div className="transcription-cols">
          <div>
            <b style={{ color: textColor }}>Original Text:</b>
            <textarea 
              readOnly 
              value={transcriptionData.transcription} 
              rows={4}
              style={{
                backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
                color: textColor,
                borderColor: theme === 'dark' ? '#374151' : '#d1d5db'
              }}
            />
          </div>
          <div>
            <b style={{ color: textColor }}>English Translation:</b>
            <textarea 
              readOnly 
              value={transcriptionData.translation || ""} 
              rows={4}
              style={{
                backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
                color: textColor,
                borderColor: theme === 'dark' ? '#374151' : '#d1d5db'
              }}
            />
          </div>
        </div>
      ) : (
        <>
          <b style={{ color: textColor }}>Language (English):</b>
          <textarea 
            readOnly 
            value={transcriptionData.transcription} 
            rows={4}
            style={{
              backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
              color: textColor,
              borderColor: theme === 'dark' ? '#374151' : '#d1d5db'
            }}
          />
        </>
      )}
      
      {/* Analyze Button */}
      {showAnalyzeButton && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <p style={{ color: textColor, marginBottom: '0.75rem', fontSize: '0.9rem' }}>
            Click below to clean up the transcription and highlight critical information.
          </p>
          <button
            className="analyze-btn"
            onClick={onAnalyze}
            disabled={processing || !patientData}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: processing || !patientData ? 'not-allowed' : 'pointer',
              opacity: processing || !patientData ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {processing ? "Analyzing..." : "Analyze Handoff"}
          </button>
        </div>
      )}
    </div>
  );
});

TranscriptionDisplay.displayName = 'TranscriptionDisplay';

export default TranscriptionDisplay;